var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import CircularProgress from '@mui/material/CircularProgress';
import { TourProvider, useTour } from '@reactour/tour';
import Title from '../../App/components/Title';
import Breadcrumbs from '../../App/components/Breadcrumbs';
import { formatCurrencyValues } from '../../App/utils/formatter';
import { useBillingResume } from '../queries/useBillingResume';
import { useDownloadBankSlip } from '../mutations/useDownloadBankSlip';
import { useDownloadBillingSummary } from '../mutations/useDownloadBillingSummary';
import { useExportBillingReport } from '../mutations/useExportBillingReport';
import ModalInvoice from '../components/ModalInvoice';
import { buildTourSteps } from '../config/billingDetailTourSteps';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DownloadIcon from '@mui/icons-material/Download';
import IosShareIcon from '@mui/icons-material/IosShare';
import '../styles/BillingDetail.css';
function BillingDetailContent() {
    var _a, _b;
    var billing_id = useParams().billing_id;
    var location = useLocation();
    var billingIdNum = billing_id ? parseInt(billing_id) : undefined;
    var prod_url = process.env.REACT_APP_STORAGE_URL;
    var _c = useState(false), showModalInvoice = _c[0], setShowModalInvoice = _c[1];
    var _d = useBillingResume({ billingId: billingIdNum }), billingData = _d.data, isLoading = _d.isLoading;
    var bankSlipMutation = useDownloadBankSlip();
    var summaryMutation = useDownloadBillingSummary();
    var exportMutation = useExportBillingReport();
    var _e = useTour(), setTourOpen = _e.setIsOpen, setCurrentStep = _e.setCurrentStep, setSteps = _e.setSteps;
    var resumeItems = useMemo(function () {
        if (!(billingData === null || billingData === void 0 ? void 0 : billingData.resume))
            return [];
        return billingData.resume.filter(function (item) {
            return !!item && typeof item.product_name === 'string' && item.product_name.length > 0;
        });
    }, [billingData]);
    var tourSteps = useMemo(function () { return buildTourSteps(resumeItems); }, [resumeItems]);
    var grouped = useMemo(function () {
        var blocks = [];
        for (var _i = 0, resumeItems_1 = resumeItems; _i < resumeItems_1.length; _i++) {
            var item = resumeItems_1[_i];
            var lastBlock = blocks[blocks.length - 1];
            if (lastBlock && lastBlock.type === item.type) {
                lastBlock.items.push(item);
            }
            else {
                blocks.push({ type: item.type, items: [item] });
            }
        }
        var seenTypes = new Set();
        return blocks.map(function (block) {
            var isFirstOfType = !seenTypes.has(block.type);
            seenTypes.add(block.type);
            return __assign(__assign({}, block), { isFirstOfType: isFirstOfType });
        });
    }, [resumeItems]);
    var _f = useMemo(function () {
        if (!(billingData === null || billingData === void 0 ? void 0 : billingData.pdf_path)) {
            return { nfse_link: '#', xml_link: '#' };
        }
        if (billingData.pdf_path.indexOf('focusnfe') === -1) {
            return {
                nfse_link: prod_url + billingData.pdf_path,
                xml_link: prod_url + billingData.xml_path,
            };
        }
        return {
            nfse_link: billingData.pdf_path,
            xml_link: billingData.xml_path,
        };
    }, [billingData, prod_url]), nfse_link = _f.nfse_link, xml_link = _f.xml_link;
    var handleStartTour = function () {
        if (tourSteps.length === 0)
            return;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSteps === null || setSteps === void 0 ? void 0 : setSteps(tourSteps);
        setCurrentStep(0);
        setTourOpen(true);
    };
    var breadcrumbsItens = [
        { name: 'Minhas faturas', route: '/billings' },
        { name: 'Detalhes da fatura', route: "/billings/".concat(billing_id) },
    ];
    return (_jsxs("div", __assign({ className: "content-internal-padding" }, { children: [_jsxs("div", __assign({ className: "billing-detail-header" }, { children: [_jsxs("div", __assign({ className: "billing-detail-title-group" }, { children: [_jsx(Title, { text: "Detalhes da fatura", variableText: (_b = (_a = location.state) === null || _a === void 0 ? void 0 : _a.referenceDate) !== null && _b !== void 0 ? _b : '', hr: false }), _jsx(Breadcrumbs, { itens: breadcrumbsItens })] })), _jsxs("div", __assign({ className: "billing-detail-actions" }, { children: [_jsxs("button", __assign({ className: "button-with-blue-border", onClick: function () { return setShowModalInvoice(true); } }, { children: [_jsx(ReceiptLongIcon, {}), "Nota Fiscal"] })), _jsxs("button", __assign({ className: "button-with-blue-border", disabled: summaryMutation.isLoading || billingIdNum === undefined, onClick: function () { return billingIdNum !== undefined && summaryMutation.mutate(billingIdNum); } }, { children: [_jsx(DownloadIcon, {}), summaryMutation.isLoading ? '...' : 'Baixar Fatura'] })), _jsxs("button", __assign({ className: "button-with-blue-border", disabled: exportMutation.isLoading || billingIdNum === undefined, onClick: function () { return billingIdNum !== undefined && exportMutation.mutate(billingIdNum); } }, { children: [_jsx(IosShareIcon, {}), exportMutation.isLoading ? '...' : 'Exportar Relatório'] })), _jsx("button", __assign({ className: "blue-button", disabled: bankSlipMutation.isLoading || billingIdNum === undefined, onClick: function () { return billingIdNum !== undefined && bankSlipMutation.mutate(billingIdNum); } }, { children: bankSlipMutation.isLoading ? '...' : 'Pagar' }))] }))] })), _jsx("div", __assign({ className: "container-billing-detail", "data-id": "billing-grid" }, { children: isLoading ? (_jsx("div", __assign({ className: "billing-detail-loading" }, { children: _jsx(CircularProgress, {}) }))) : (_jsxs("div", __assign({ "data-id": "billing-links" }, { children: [_jsxs("div", __assign({ className: "billing-detail-summary-row" }, { children: [_jsxs("div", __assign({ className: "billing-total-card", "data-tour": "total-card" }, { children: [_jsx("p", __assign({ className: "billing-total-label" }, { children: "Total a pagar" })), _jsxs("div", __assign({ className: "d-flex" }, { children: [_jsxs("p", __assign({ className: "billing-total-currency" }, { children: [" R$", ' '] })), _jsx("p", __assign({ className: "billing-total-value" }, { children: _jsx("strong", { children: (billingData === null || billingData === void 0 ? void 0 : billingData.total) !== undefined &&
                                                            billingData.total.toLocaleString('pt-br', {
                                                                minimumFractionDigits: 2,
                                                            }) }) }))] }))] })), _jsx("div", __assign({ className: "billing-tour-card" }, { children: _jsxs("div", __assign({ className: "billing-tour-wrapper" }, { children: [_jsx("h3", __assign({ className: "billing-tour-title" }, { children: "D\u00FAvida sobre os valores?" })), _jsx("p", __assign({ className: "billing-tour-description" }, { children: "Preparamos um guia interativo para explicar cada item do seu faturamento detalhadamente, incluindo m\u00E9tricas de consumo e regras de neg\u00F3cio." })), _jsxs("button", __assign({ type: "button", className: "billing-tour-button", onClick: handleStartTour }, { children: [_jsx(SchoolIcon, {}), " Iniciar Tour de Explica\u00E7\u00E3o"] }))] })) }))] })), _jsx("div", __assign({ className: "billing-detail-table-card" }, { children: resumeItems.length === 0 ? (_jsx("div", __assign({ className: "billing-detail-no-data" }, { children: _jsx("strong", { children: "PARA MAIS DETALHES, BAIXE A FATURA" }) }))) : (_jsxs("table", __assign({ className: "billing-detail-table" }, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Categoria/Produto" }), _jsx("th", __assign({ "data-tour": "col-full-period" }, { children: "Consumo m\u00EAs cheio (Tokens)" })), _jsx("th", __assign({ "data-tour": "col-pro-rata" }, { children: "Consumo pr\u00F3-rata (Tokens)" })), _jsx("th", { children: "Valor total" })] }) }), grouped.map(function (block, blockIndex) { return (_jsx("tbody", __assign({ "data-tour": block.isFirstOfType ? "group-".concat(block.type) : undefined }, { children: block.items.map(function (item, index) { return (_jsx(BillingResumeRow, { item: item }, "".concat(item.product_name, "-").concat(blockIndex, "-").concat(index))); }) }), "".concat(block.type, "-").concat(blockIndex))); })] }))) }))] }))) })), showModalInvoice && (_jsx(ModalInvoice, { show: showModalInvoice, closeModal: function () { return setShowModalInvoice(false); }, nfse_link: nfse_link, xml_link: xml_link }))] })));
}
function BillingResumeRow(_a) {
    var _b, _c;
    var item = _a.item;
    var hasTotal = item.total !== null && item.total !== undefined;
    var isDiscount = item.type === 'discount' || (hasTotal && item.total < 0);
    var totalDisplay;
    if (!hasTotal) {
        totalDisplay = '-';
    }
    else if (isDiscount) {
        totalDisplay = "-".concat(formatCurrencyValues(Math.abs(item.total)));
    }
    else {
        totalDisplay = formatCurrencyValues(item.total);
    }
    return (_jsxs("tr", { children: [_jsx("td", { children: item.product_name }), _jsx("td", { children: (_b = item.full_period) !== null && _b !== void 0 ? _b : '-' }), _jsx("td", { children: (_c = item.partial_period) !== null && _c !== void 0 ? _c : '-' }), _jsx("td", __assign({ className: isDiscount ? 'billing-detail-discount' : '' }, { children: totalDisplay }))] }));
}
function TourPopoverContent(props) {
    var steps = props.steps, currentStep = props.currentStep, setCurrentStep = props.setCurrentStep, setIsOpen = props.setIsOpen;
    var step = steps[currentStep];
    if (!step)
        return null;
    var stepContent = typeof step.content === 'function' ? step.content(props) : step.content;
    var isFirst = currentStep === 0;
    var isLast = currentStep === steps.length - 1;
    return (_jsxs("div", { children: [_jsx("div", __assign({ style: { marginBottom: 24 } }, { children: stepContent })), _jsxs("div", __assign({ style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                } }, { children: [_jsxs("span", __assign({ style: {
                            fontSize: '0.75rem',
                            color: '#888',
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            fontWeight: 500,
                        } }, { children: ["Passo ", currentStep + 1, " de ", steps.length] })), _jsxs("div", __assign({ style: { display: 'flex', gap: 12 } }, { children: [!isFirst && (_jsx("button", __assign({ type: "button", className: "button-with-blue-border", style: {
                                    fontSize: '0.95rem',
                                    fontWeight: 'lighter',
                                    borderRadius: '8px 8px 8px 0',
                                    color: '#003682',
                                    background: 'transparent',
                                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 2px 6px 2px rgba(0, 0, 0, 0.15)',
                                    display: 'flex',
                                    padding: '8px 24px',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                }, onClick: function () { return setCurrentStep(function (s) { return (typeof s === 'number' ? s - 1 : 0); }); } }, { children: "Voltar" }))), _jsx("button", __assign({ type: "button", className: "blue-button", style: {
                                    padding: '10px 28px',
                                    fontSize: '0.95rem',
                                    fontWeight: 'lighter',
                                    borderRadius: '8px 8px 8px 0',
                                    background: '#003682',
                                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 2px 6px 2px rgba(0, 0, 0, 0.15)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }, onClick: function () {
                                    if (isLast) {
                                        setIsOpen(false);
                                        setCurrentStep(0);
                                    }
                                    else {
                                        setCurrentStep(function (s) { return (typeof s === 'number' ? s + 1 : 1); });
                                    }
                                } }, { children: isLast ? 'Concluir' : 'Continuar' }))] }))] }))] }));
}
function BillingDetail() {
    return (_jsx(TourProvider, __assign({ steps: [], showBadge: false, showDots: false, showNavigation: false, ContentComponent: TourPopoverContent, afterOpen: function () {
            document.body.style.overflow = 'hidden';
        }, beforeClose: function () {
            document.body.style.overflow = '';
        }, styles: {
            popover: function (base) { return (__assign(__assign({}, base), { borderRadius: 12, padding: '28px 32px', maxWidth: 480, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)' })); },
            close: function (base) { return (__assign(__assign({}, base), { top: 20, right: 20, width: 14, height: 14, color: '#666' })); },
        } }, { children: _jsx(BillingDetailContent, {}) })));
}
export default BillingDetail;
